import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SegmentedReadAloud = ({ text, speechId, serviceType = "ksg" }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [segments, setSegments] = useState([]);
  const [audioCache, setAudioCache] = useState({}); // In-memory cache for current session
  const [isLoading, setIsLoading] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [volume, setVolume] = useState(1.0); // Volume from 0 to 1
  
  // New states for explicit generation
  const [generationStatus, setGenerationStatus] = useState('checking'); // 'checking', 'not_generated', 'generating', 'ready', 'error'
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 });
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [generationError, setGenerationError] = useState(null);

  // Split text into segments at paragraph boundaries, ~4000 characters each
  useEffect(() => {
    if (!text) return;
    
    const maxChunkSize = 4000; // OpenAI TTS limit is 4096
    const paragraphs = text.split(/\n\n+/);
    const chunks = [];
    let currentChunk = '';
    
    paragraphs.forEach((para) => {
      // If adding this paragraph keeps us under limit, add it
      if (currentChunk.length + para.length < maxChunkSize) {
        currentChunk += (currentChunk ? '\n\n' : '') + para;
      } else {
        // Save current chunk if it exists
        if (currentChunk) {
          chunks.push(currentChunk.trim());
        }
        // Start new chunk with this paragraph
        currentChunk = para;
      }
    });
    
    // Don't forget the last chunk
    if (currentChunk) {
      chunks.push(currentChunk.trim());
    }
    
    // Filter out any empty segments
    const validChunks = chunks.filter(chunk => chunk && chunk.trim().length > 0);
    
    setSegments(validChunks);
    setAudioCache({}); // Clear cache when text changes
  }, [text]);

  // Check if audio already exists after segments are created
  useEffect(() => {
    if (!speechId || segments.length === 0) {
      setGenerationStatus('not_generated');
      return;
    }
    
    // Check if first segment exists in database cache
    checkAudioExists();
  }, [segments, speechId]);

  const checkAudioExists = async () => {
    if (!speechId || segments.length === 0) {
      setGenerationStatus('not_generated');
      return;
    }
    
    setGenerationStatus('checking');
    
    try {
      // Try to fetch the first segment - check if it was cached
      const response = await axios.post(
        `${API}/api/tts/generate`,
        {
          text: segments[0],
          voice: 'nova',
          speech_id: speechId,
          segment_index: 0,
          service_type: serviceType
        },
        { 
          withCredentials: true,
          responseType: 'json'
        }
      );

      // Check if the audio was retrieved from cache
      if (response.data.was_cached) {
        setGenerationStatus('ready');
      } else {
        // First segment was just generated - need to generate the rest
        setGenerationStatus('not_generated');
      }
    } catch (error) {
      // If error, audio doesn't exist yet
      console.error('❌ Audio check failed:', error.response?.data || error.message);
      setGenerationStatus('not_generated');
    }
  };

  const generateAudio = async (segmentIndex) => {
    // Validate segment exists and has content
    if (!segments[segmentIndex] || segments[segmentIndex].trim().length === 0) {
      console.error(`❌ Segment ${segmentIndex} is empty or undefined`);
      throw new Error(`Segment ${segmentIndex} has no content`);
    }
    
    // Check in-memory cache first (fast path for current session)
    if (audioCache[segmentIndex]) {
      return audioCache[segmentIndex];
    }

    setIsLoading(true);
    try {
      // Request with speech_id and segment_index for persistent database caching
      const requestData = {
        text: segments[segmentIndex],
        voice: 'nova'
      };
      
      // Add speech_id, segment_index, and service_type if available (enables database caching)
      if (speechId) {
        requestData.speech_id = speechId;
        requestData.segment_index = segmentIndex;
        requestData.service_type = serviceType;
      } else {
      }
      
      const response = await axios.post(
        `${API}/api/tts/generate`,
        requestData,
        { 
          withCredentials: true,
          responseType: 'json' // Ensure we get JSON response
        }
      );

      const audioDataUrl = response.data.audio_url;
      
      // Convert data URL to Blob for better compatibility
      if (audioDataUrl.startsWith('data:audio/mpeg;base64,')) {
        const base64Data = audioDataUrl.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'audio/mpeg' });
        const objectUrl = URL.createObjectURL(blob);
        
        // Cache the Object URL
        setAudioCache(prev => ({
          ...prev,
          [segmentIndex]: objectUrl
        }));

        return objectUrl;
      }

      return audioDataUrl;
    } catch (error) {
      console.error('❌ Error generating audio:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const generateAllSegments = async () => {
    setGenerationStatus('generating');
    setShowGenerationModal(true);
    setGenerationProgress({ current: 0, total: segments.length });
    setGenerationError(null);

    try {
      
      for (let i = 0; i < segments.length; i++) {
        setGenerationProgress({ current: i, total: segments.length });
        
        // Generate this segment (will be cached in database)
        await generateAudio(i);
        
        // Small delay between segments to avoid overwhelming the API
        if (i < segments.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      setGenerationProgress({ current: segments.length, total: segments.length });
      setGenerationStatus('ready');
      
      // Close modal after a brief success message
      setTimeout(() => {
        setShowGenerationModal(false);
      }, 1500);
      
    } catch (error) {
      console.error('❌ Error during generation:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        speechId: speechId,
        serviceType: serviceType,
        segmentCount: segments.length
      });
      
      // More detailed error message
      const errorMessage = error.response?.data?.detail 
        || error.message 
        || 'Failed to generate audio. Please try again.';
      
      setGenerationError(errorMessage);
      setGenerationStatus('error');
    }
  };

  const playSegment = async (index) => {
    if (index >= segments.length) {
      setIsPlaying(false);
      setCurrentSegment(0);
      return;
    }

    try {
      const audioDataUrl = await generateAudio(index);
      
      // Create audio element
      const audio = new Audio(audioDataUrl);
      audio.volume = volume; // Set volume explicitly
      setCurrentAudio(audio);

      audio.onloadeddata = () => {
      };

      audio.onended = () => {
        if (index < segments.length - 1) {
          setCurrentSegment(index + 1);
          playSegment(index + 1);
        } else {
          setIsPlaying(false);
          setCurrentSegment(0);
        }
      };

      audio.onerror = (event) => {
        console.error('❌ Audio playback error:', event);
        console.error('Audio error details:', audio.error);
        setIsPlaying(false);
        setCurrentSegment(0);
      };

      // Attempt to play
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
          })
          .catch(error => {
            console.error('❌ Play failed:', error);
            setIsPlaying(false);
          });
      }
    } catch (error) {
      console.error('❌ Error playing segment:', error);
      setIsLoading(false);
      setIsPlaying(false);
    }
  };

  const handlePlay = async () => {
    setIsPlaying(true);
    await playSegment(currentSegment);
  };

  const handlePause = () => {
    if (currentAudio) {
      currentAudio.pause();
      setIsPlaying(false);
    }
  };

  const handleStop = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentSegment(0);
  };

  const handleSegmentSelect = async (index) => {
    if (currentAudio) {
      currentAudio.pause();
    }
    setCurrentSegment(index);
    setIsPlaying(true);
    await playSegment(index);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (currentAudio) {
      currentAudio.volume = newVolume;
    }
  };

  if (!text || segments.length === 0) {
    return null;
  }

  // Show different UI based on generation status
  return (
    <>
      {/* Generation Modal */}
      {showGenerationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            {/* Show different content based on status */}
            {generationStatus === 'generating' && (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  🎵 Generating Speech Audio...
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Please wait while we create your audio. This will take 1-3 minutes.
                </p>
                <div className="mb-2">
                  <div className="flex justify-between text-sm text-gray-700 mb-1">
                    <span>Processing segment {generationProgress.current + 1} of {generationProgress.total}</span>
                    <span>{Math.round(((generationProgress.current + 1) / generationProgress.total) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${((generationProgress.current + 1) / generationProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  ⏳ Estimated time remaining: {Math.max(0, (generationProgress.total - generationProgress.current - 1) * 6)} seconds
                </p>
              </>
            )}

            {generationStatus === 'ready' && (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  ✅ Audio Ready!
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Your audio has been generated and is ready to play!
                </p>
              </>
            )}

            {generationStatus === 'error' && (
              <>
                <h3 className="text-xl font-bold text-red-900 mb-2">
                  ❌ Generation Failed
                </h3>
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{generationError || 'Failed to generate audio. Please try again.'}</p>
                  <button
                    onClick={() => {
                      setShowGenerationModal(false);
                      setGenerationStatus('not_generated');
                      setGenerationError(null);
                    }}
                    className="mt-3 px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium"
                  >
                    Close and Try Again
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Main TTS Control */}
      <div className="border border-blue-200 rounded-lg p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
        {/* Checking Status */}
        {generationStatus === 'checking' && (
          <div className="text-center py-4">
            <div className="animate-spin inline-block w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full mb-2"></div>
            <p className="text-sm text-blue-700 font-medium">🎵 Preparing your premium audio experience...</p>
          </div>
        )}

        {/* Not Generated - Show Generate Button */}
        {generationStatus === 'not_generated' && (
          <div className="text-center py-4">
            <h4 className="text-base font-bold text-blue-900 mb-2">
              🎙️ Premium Audio Experience Included
            </h4>
            <p className="text-sm text-blue-700 mb-4 max-w-lg mx-auto">
              Listen to your content with professional AI narration powered by OpenAI (voice: Nova). 
              Generate once, replay <strong>unlimited times</strong> - this premium feature is included with your service.
            </p>
            <button
              onClick={generateAllSegments}
              className="px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              ⚡ Generate Audio For This {serviceType.toUpperCase()} Report
            </button>
            <p className="text-xs text-blue-600 mt-3">
              🎧 Ready in {Math.ceil(segments.length * 6 / 60)} {Math.ceil(segments.length * 6 / 60) === 1 ? 'minute' : 'minutes'} • {segments.length} segments • Unlimited replays
            </p>
          </div>
        )}

        {/* Error Status */}
        {generationStatus === 'error' && (
          <div className="text-center py-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="text-base font-bold text-red-900 mb-2">❌ Generation Failed</h4>
              <p className="text-sm text-red-700 mb-3">{generationError || 'An error occurred during audio generation.'}</p>
              <button
                onClick={() => setGenerationStatus('not_generated')}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Ready - Show Player Controls */}
        {generationStatus === 'ready' && (
          <>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-sm font-bold text-blue-900">
                  🎙️ Listen to Speech
                </h4>
                <p className="text-xs text-green-600 mt-0.5 flex items-center gap-1">
                  ✅ Audio Ready - Play anytime!
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Volume Control */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-blue-700">🔊</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-20 h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    title="Volume"
                  />
                  <span className="text-xs text-blue-700 w-8">{Math.round(volume * 100)}%</span>
                </div>
                {/* Play Controls */}
                <div className="flex gap-2">
                  {!isPlaying ? (
                    <button
                      onClick={handlePlay}
                      disabled={isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 shadow-sm"
                    >
                      {isLoading ? '⏳ Loading...' : '▶️ Play'}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handlePause}
                        className="px-3 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors shadow-sm"
                      >
                        ⏸️ Pause
                      </button>
                      <button
                        onClick={handleStop}
                        className="px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm"
                      >
                        ⏹️ Stop
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Progress indicator */}
            {segments.length > 1 && (
              <div className="mb-3">
                <div className="flex justify-between text-xs text-blue-700 mb-1">
                  <span>Segment {currentSegment + 1} of {segments.length}</span>
                  <span>{Math.round(((currentSegment + 1) / segments.length) * 100)}% complete</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2.5">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${((currentSegment + 1) / segments.length) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Segment navigation */}
            {segments.length > 1 && (
              <div>
                <p className="text-xs text-blue-700 font-semibold mb-2">Jump to segment:</p>
                <div className="flex flex-wrap gap-2">
                  {segments.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => handleSegmentSelect(index)}
                      disabled={isLoading}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors shadow-sm ${
                        index === currentSegment
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-blue-700 border border-blue-300 hover:bg-blue-100'
                      } disabled:opacity-50`}
                      title={`Jump to segment ${index + 1}`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default SegmentedReadAloud;
